import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useDraggable } from '@dnd-kit/core';
import { forwardRef, memo, useCallback, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { Icon, Image, Text } from '../../ui';
import { AssetActionButtons } from './AssetActionButtons';
import { StyleContentWrapper, StyledImage, StyledOverlayWrapper, StyledTextActionButtons, StyledTextLabelWrapper, StyledTextWrapper, StyledWrapper, } from './AssetCard.style';
export const sourceFallback = 'https://static.desygner.com/assets/img/placeholders/imageplaceholder_new.svg';
export const AssetCard = memo(forwardRef(function AssetCard({ title, draggable, actionButtons, source, onClick, data }, ref) {
    const theme = useTheme();
    const showOverlay = !!title || !!actionButtons;
    const [activeSource, setActiveSource] = useState(source[0]);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
    const [isError, setIsError] = useState(false);
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const onClickHandler = useCallback((action) => (event) => {
        event.stopPropagation();
        event.preventDefault();
        onClick({
            action,
            source: activeSource,
            details: Object.assign(Object.assign({}, (data !== null && data !== void 0 ? data : {})), { size: dimensions }),
        });
    }, [onClick, activeSource, data, dimensions]);
    const onLoadHandler = useCallback((event, source) => {
        const { width, height } = event.currentTarget.getBoundingClientRect();
        setIsImageLoaded(true);
        setActiveSource((prev) => {
            return prev === source ? prev : source;
        });
        setDimensions({ width, height });
    }, [setDimensions]);
    const onErrorHandler = useCallback(() => {
        setIsImageLoaded(true);
        if (data &&
            typeof data === 'object' &&
            !Array.isArray(data) &&
            'type' in data &&
            (data === null || data === void 0 ? void 0 : data.type) === 'Video')
            return;
        setIsError(true);
    }, [setIsError, data]);
    const draggableHandlers = useDraggable({
        id: activeSource,
        data: {
            type: ['canvas'],
            source: activeSource,
            title,
            details: data,
        },
    });
    const draggableData = useMemo(() => {
        return draggable
            ? Object.assign(Object.assign({ ref: draggableHandlers.setNodeRef }, draggableHandlers.listeners), draggableHandlers.attributes) : {};
    }, [draggable, draggableHandlers]);
    if (isError)
        return null;
    return (_jsxs(StyledWrapper, Object.assign({}, draggableData, { onClick: onClickHandler(), isImageLoaded: isImageLoaded, children: [_jsx(StyledImage, { children: _jsx(Image, { source: source, sourceFallback: sourceFallback, rounded: true, onLoad: onLoadHandler, onError: onErrorHandler, ref: ref }) }), showOverlay && (_jsx(StyledOverlayWrapper, { children: _jsxs(StyleContentWrapper, { children: [actionButtons && (_jsx(AssetActionButtons, { color: theme.Color.NeutralsFixed.Paper, type: 'image', buttons: actionButtons, onClick: onClickHandler })), title && (_jsx(Text, { size: "Body", color: theme.Color.NeutralsFixed.Paper, truncate: true, maxWidth: theme.Card.Size.Inline.width - theme.Spacing.Size.XSmall * 2, children: title }))] }) }))] })));
}));
export const TextCard = memo(forwardRef(function TextCard({ source, draggable, title, icon, variant, actionButtons, onClick, data, }, ref) {
    const theme = useTheme();
    const textSource = Array.isArray(source) ? source[0] : source;
    const onClickHandler = useCallback((action) => (event) => {
        event.stopPropagation();
        event.preventDefault();
        onClick({
            action,
            source: textSource,
            details: data,
        });
    }, [data, onClick, textSource]);
    const draggableHandlers = useDraggable({
        id: textSource,
        data: {
            type: ['canvas'],
            source,
            title: source,
            details: data,
        },
    });
    const draggableData = useMemo(() => {
        return draggable
            ? Object.assign(Object.assign({ ref: draggableHandlers.setNodeRef }, draggableHandlers.listeners), draggableHandlers.attributes) : {};
    }, [draggable, draggableHandlers]);
    return (_jsxs(StyledWrapper, Object.assign({}, draggableData, { onClick: onClickHandler(), type: "text", children: [_jsx(StyleContentWrapper, { type: "text", variant: variant, children: _jsxs(StyledTextWrapper, { children: [icon && _jsx(Icon, { name: icon, color: theme.Color.Neutrals.Text }), _jsx(Text, { ref: ref, size: "Body", children: textSource.replace(/\u00A0/g, ' ') })] }) }), (title || actionButtons) && (_jsxs(StyledTextActionButtons, { children: [title && (_jsx(StyledTextLabelWrapper, { children: _jsx(Text, { size: "SmallHeavy", color: theme.Color.Neutrals.Text, truncate: true, children: title }) })), actionButtons && (_jsx(AssetActionButtons, { color: theme.Color.Neutrals.Dark, type: "text", buttons: actionButtons, onClick: onClickHandler }))] }))] })));
}));
//# sourceMappingURL=AssetCard.js.map